import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import {
  BackofficeMetricUnit,
  FccOperatorAvailabilityType,
} from '@backoffice-frontend/graphql'
import { useLocalStorage } from '@backoffice-frontend/hooks'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
} from 'react'
import useSound from 'use-sound'

type AlertSoundsContextValue = {
  alertSoundsEnabled: boolean
  toggleAlertSounds: () => void
  playAlertSound: () => void
}

export const AlertSoundsContext = createContext<AlertSoundsContextValue | null>(
  null,
)

const THROTTLE_MS = 1000

export const useAlertSoundsProvider = (): AlertSoundsContextValue => {
  const { availability, loading } = useAdOperatorAvailability()
  const { trackMetric } = useMetricTracking()
  const isOnDuty = availability !== FccOperatorAvailabilityType.Offline
  const prevIsOnDutyRef = useRef<boolean | undefined>(undefined)
  const lastPlayTimeRef = useRef(0)
  const stateStartTimeRef = useRef(Date.now())

  const [alertSoundsEnabled, setAlertSoundsEnabled] = useLocalStorage(
    'operatorAlertSoundsEnabled',
    isOnDuty,
  )

  const [play] = useSound('/assets/sounds/prompt-notification.mp3', {
    volume: 1,
  })

  const trackStateDuration = useEffectEvent(
    (currentState: boolean, dutyStatus: boolean) => {
      const duration = Date.now() - stateStartTimeRef.current
      trackMetric({
        metricName: 'AlertSoundsStateDuration',
        value: duration,
        unit: BackofficeMetricUnit.Milliseconds,
        dimensions: [
          { name: 'state', value: currentState ? 'enabled' : 'disabled' },
          { name: 'dutyStatus', value: dutyStatus ? 'onDuty' : 'offDuty' },
        ],
      })
      stateStartTimeRef.current = Date.now()
    },
  )

  const onPlay = useEffectEvent(() => play())

  useEffect(() => {
    // Ignore while loading availability
    if (loading) return

    if (prevIsOnDutyRef.current === undefined) {
      // Only record the first on duty status,
      // don't overwrite the persisted sound preference
      prevIsOnDutyRef.current = isOnDuty
    } else if (prevIsOnDutyRef.current !== isOnDuty) {
      trackStateDuration(alertSoundsEnabled, prevIsOnDutyRef.current)
      setAlertSoundsEnabled(isOnDuty)
      prevIsOnDutyRef.current = isOnDuty
    }
  }, [loading, isOnDuty, alertSoundsEnabled, setAlertSoundsEnabled])

  const toggleAlertSounds = useCallback(() => {
    setAlertSoundsEnabled(prev => {
      trackStateDuration(prev, isOnDuty)
      return !prev
    })
  }, [isOnDuty, setAlertSoundsEnabled])

  const playAlertSound = useCallback(() => {
    const now = Date.now()
    if (alertSoundsEnabled && now - lastPlayTimeRef.current > THROTTLE_MS) {
      lastPlayTimeRef.current = now
      onPlay()
    }
  }, [alertSoundsEnabled])

  return useMemo(
    () => ({
      alertSoundsEnabled,
      toggleAlertSounds,
      playAlertSound,
    }),
    [alertSoundsEnabled, toggleAlertSounds, playAlertSound],
  )
}

export const useAlertSounds = (): AlertSoundsContextValue => {
  const context = useContext(AlertSoundsContext)
  if (!context) {
    throw new Error('useAlertSounds must be used within AlertSoundsProvider')
  }
  return context
}
